import { modelHelperModule } from './model-helper';
import { languages } from '../constants/languages';
import { camelCase } from '../utils/string-utils';
import { defineValue } from '../utils/object-utils/object-utils';

export const TEXT_QUESTION = 'QuizTextQuestion';
export const WEIGHTED_CHOICE_QUESTION = 'QuizWeightedChoiceQuestion';

export const BASE_TYPES = {
  [TEXT_QUESTION]: 'text',
  [WEIGHTED_CHOICE_QUESTION]: 'multiple-choice',
};

// used to configure default options before injection
export const DEFAULT_OPTS = {
  [TEXT_QUESTION]: {
    subtype: 'short',
    language: 'text',
    lengthValidation: {
      enabled: false,
      expectedLength: 0,
    },
  },
  [WEIGHTED_CHOICE_QUESTION]: {
    shuffle: false,
    automax: true,
    autosum: true,
  },
};

export const TEXT_SUBTYPES_ORDERED = [
  { id: 'short', label: 'Short Answer' },
  { id: 'lplain', label: 'Long Answer (Plain Text)' },
  { id: 'long', label: 'Long Answer (Markdown-Enabled)' },
  { id: 'essayplain', label: 'Essay (Plain Text)' },
  { id: 'essay', label: 'Essay (Markdown-Enabled)' },
  { id: 'scode', label: 'Code (Short)' },
  { id: 'lcode', label: 'Code (Multiline)' },
];

export const TEXT_SUBTYPES = {};
TEXT_SUBTYPES_ORDERED.forEach((t) => {
  TEXT_SUBTYPES[t.id] = t.label;
});

export const MC_SUBTYPES = {
  mcma: 'Multiple Choice (Multiple Answer)',
  mcsa: 'Multiple Choice (Single Answer)',
};

export const INPUT_TYPE_MAP = {
  short: 'text',
  lplain: 'text',
  long: 'markdown',
  essayplain: 'text',
  essay: 'markdown',
  scode: 'code',
  lcode: 'code',
  mcma: 'checkbox',
  mcsa: 'radio',
};

export const MATCHER_LABELS = {
  includes: 'Includes Text',
  includesIncorrect: 'Should Include Text',
  notincludes: 'Excludes Text',
  notincludesIncorrect: 'Should Exclude Text',
  exact: 'Equals Exactly',
  exactIncorrect: 'Should Equal',
  notexact: 'Does Not Equal',
  notexactIncorrect: 'Should Not Equal',
  regex: 'Matches Pattern',
  regexIncorrect: 'Should Match Pattern',
  notregex: 'Does Not Match Pattern',
  notregexIncorrect: 'Should Not Match Pattern',
};

export const MATCHER_SHORT_LABELS = {
  includes: 'Includes',
  notincludes: 'Excludes',
  exact: 'Equals',
  notexact: 'Is Not',
  regex: 'Matches',
  notregex: 'No Match',
};

export const MATCHER_TYPES = [
  { label: MATCHER_LABELS.includes, type: 'includes', negate: false },
  { label: MATCHER_LABELS.notincludes, type: 'includes', negate: true },
  { label: MATCHER_LABELS.exact, type: 'exact', negate: false },
  { label: MATCHER_LABELS.notexact, type: 'exact', negate: true },
  { label: MATCHER_LABELS.regex, type: 'regex', negate: false },
  { label: MATCHER_LABELS.notregex, type: 'regex', negate: true },
];

export function cleanUpQuizQuestion(instance) {
  if (
    instance.$type === 'QuizMultipleChoiceQuestion' &&
    instance.choices &&
    angular.isString(instance.choices[0])
  ) {
    // convert to new weighted format
    // if this question is saved, it'll get replaced at that time, otherwise we'll just pretend it's been updated locally
    instance.$type = WEIGHTED_CHOICE_QUESTION;
    instance.choices = instance.choices.map((c) => ({
      label: c,
      value: 0, // TODO: determine default non-answer value?
    }));
    instance.maxSum = 0;
    if (instance.answers) {
      instance.answers.forEach((a) => (instance.choices[+a].value = 1));
      instance.maxSum = instance.answers.length;
      delete instance.answers;
    }
  }

  // handle case swaps in options, because the API doesn't
  const opts = (instance.options = instance.options || {});
  Object.keys(opts).forEach((k) => {
    if (/_/.test(k)) {
      opts[camelCase(k)] = opts[k];
      delete opts[k];
    }
  });

  angular.forEach(DEFAULT_OPTS[instance.$type] || {}, (v, k) => {
    if (opts[k] === undefined) {
      opts[k] = angular.copy(v);
    }
  });

  if (instance.$type === WEIGHTED_CHOICE_QUESTION) {
    instance.weightedScoring = instance.weightedScoring || false;
  }

  return instance;
}

export const quizQuestionModelModule = angular
  .module('Q.models.quiz-question', [modelHelperModule])

  .factory('QuizQuestion', function (ModelHelper, $filter, $injector) {
    const QuizQuestion = ModelHelper.defineResource({
      name: 'QuizQuestion',
      endpoint: 'quiz_questions',

      cleanAfterDestroy: {
        parent: 'Challenge',
        collection: 'questions',
      },

      // Convert API-style answers into something we can work on locally
      beforeInject: function QuizQuestion$beforeInject(resource, instances) {
        if (!Array.isArray(instances)) {
          instances = [instances];
        }
        instances.forEach(cleanUpQuizQuestion);
      },
      // Convert local formatted answers into API-style
      serialize(resource, data) {
        // TODO: always include the $type!
        QuizQuestion.syncChoices(data);
        return ModelHelper.serialize(resource, data);
      },

      methods: {
        baseType() {
          return BASE_TYPES[this.$type];
        },
        isText() {
          return this.$type === TEXT_QUESTION;
        },
        isMultipleChoice() {
          return this.$type === WEIGHTED_CHOICE_QUESTION;
        },
        baseTypeLabel() {
          if (this.isText()) {
            return 'Free Text Question';
          } else if (this.isMultipleChoice()) {
            return 'Multiple Choice Question';
          }
          return 'Question';
        },
        answerType() {
          if (this.isText()) {
            return this.options.subtype || 'short';
          } else if (this.isMultipleChoice()) {
            return this.maxSelections === 1 ? 'mcsa' : 'mcma';
          }
        },
        answerTypeLabel() {
          if (this.isCode()) {
            return this.languageLabel() + TEXT_SUBTYPES[this.answerType()];
          } else if (this.isText()) {
            return TEXT_SUBTYPES[this.answerType()];
          } else if (this.isMultipleChoice()) {
            return MC_SUBTYPES[this.answerType()];
          }
        },
        hasLanguage() {
          let lang = this.options.language;
          return !!lang && lang !== 'text' && !!languages[lang];
        },
        editorLanguage() {
          if (this.isCode()) {
            return this.options.language || 'text';
          } else {
            return this.isMultiline() ? 'markdown' : 'text';
          }
        },
        languageLabel() {
          let lang = this.options.language || '';
          if (lang) {
            lang = languages.toName(lang) + ' ';
          }
          return lang;
        },
        inputType() {
          return INPUT_TYPE_MAP[this.answerType()] || 'text';
        },
        isAnswerType(...types) {
          return types.includes(this.answerType());
        },
        isPlain() {
          return this.isAnswerType('short', 'lplain', 'essayplain');
        },
        isMarkdown() {
          return this.isAnswerType('long', 'essay');
        },
        isMultiline() {
          return this.isEssay() || this.isAnswerType('long', 'lcode');
        },
        isEssay() {
          return this.isAnswerType('essay', 'essayplain');
        },
        isCode() {
          return this.isAnswerType('scode', 'lcode');
        },
        isMultipleAnswer() {
          return this.answerType() === 'mcma';
        },
        isAnswer(index, solution) {
          if (solution && solution.values) {
            return solution.values.indexOf(index) > -1;
          } else {
            return this.choices[index].value > 0;
          }
        },
        isCorrect(index, solution) {
          if (!solution) return false;
          return (
            solution.score >= 100 ||
            this.isAnswer(index) === this.isAnswer(index, solution)
          );
        },
        maxAnswers() {
          const question = this;
          if (question.isMultipleChoice()) {
            return question.choices.filter((a) => a.value > 0).length;
          } else if (question.isText()) {
            return (question.matchers || []).length;
          }
        },
        maxCorrectSum() {
          return this.choices
            .map((c) => c.value)
            .filter((v) => v > 0)
            .sort((a, b) => b - a)
            .slice(0, this.maxSelections || 1)
            .reduce((a, b) => a + b, 0);
        },
        maxWeight() {
          return (
            this.choices
              .map((c) => c.value)
              .filter((v) => v > 0)
              .sort((a, b) => b - a)[0] || 0
          );
        },
        shouldDisable(solutionAnswers, $index) {
          solutionAnswers = solutionAnswers || {};
          const remaining = Math.max(
            0,
            this.updateMaxSelections() - (solutionAnswers.values || []).length,
          );
          const selected = (solutionAnswers.$mcmaValues || [])[$index];
          return !remaining && !selected;
        },
        updateMaxSelections(force) {
          const question = this;
          if (!question.isMultipleChoice()) return question.maxSelections;

          if (force || !question.maxSelections) {
            const totalAnswers = (question.choices || []).length;
            if (question.options.automax === 'all') {
              question.maxSelections = totalAnswers;
            } else if (question.options.automax) {
              question.maxSelections = question.maxAnswers();
            }
            // ensure we don't an invalid selection
            question.maxSelections = Math.max(
              1,
              Math.min(question.maxSelections, totalAnswers),
            );
          }

          return question.maxSelections;
        },
        remainingSelectionsMessage(solutionAnswers) {
          solutionAnswers = solutionAnswers || {};
          const maxSelections = this.updateMaxSelections();
          const remaining = Math.max(
            0,
            maxSelections - (solutionAnswers.values || []).length,
          );
          let msg = 'Max allowed answers selected';
          if (maxSelections === this.choices.length) {
            msg = 'Choose all correct answers below';
          } else if (remaining === maxSelections) {
            msg = `Choose up to <strong>${remaining}</strong> answers below`;
          } else if (remaining > 0) {
            msg = `Choose up to <strong>${remaining}</strong> more answer${remaining === 1 ? '' : 's'}`;
          }
          return msg;
        },
        updateMaxSum(force) {
          const question = this;
          if (question.isMultipleChoice() && (force || !question.maxSum)) {
            if (question.options.autosum === 'highest') {
              question.maxSum = question.maxWeight();
            } else if (question.options.autosum) {
              question.maxSum = question.maxCorrectSum();
            }
            // ensure we don't an invalid selection
            question.maxSum = Math.max(0, question.maxSum || 0);
          }
          return question.maxSum;
        },
        optionsSummary(challenge, answer) {
          const numFilter = $injector.has('fuzzyNumberFilter')
            ? $filter('fuzzyNumber')
            : $filter('number');
          const summary = [];
          const self = this;
          let weight = angular.isNumber(+self.weight) ? +self.weight : 1;
          let score = '';
          if (weight === 0) {
            score = 'Not scored';
          } else {
            if (answer) {
              if (answer.values.length) {
                // Calculate length score if not present

                if (
                  self.options.lengthValidation &&
                  self.options.lengthValidation.enabled
                ) {
                  const answerText = answer.values[0] || '';
                  answer.lengthScore = self.calculateLengthScore(answerText);
                }
                const lengthScore = answer.lengthScore || 0; // Already between 0-1
                const accuracyScore = (answer.accuracyScore || 0) / 100; // Convert from percentage to 0-1

                // Calculate final score based on whether length validation is enabled
                let finalScore;
                if (
                  self.options.lengthValidation &&
                  self.options.lengthValidation.enabled
                ) {
                  finalScore = (accuracyScore + lengthScore) / 2; // Average of both scores
                } else {
                  finalScore = accuracyScore;
                }

                if (
                  self.options.lengthValidation &&
                  self.options.lengthValidation.enabled
                ) {
                  score = `${numFilter(finalScore * weight)} out of ${numFilter(weight)} point${weight === 1 ? '' : 's'} (Matchers: ${numFilter(accuracyScore * 100)}%, Length: ${numFilter(lengthScore * 100)}%)`;
                } else {
                  score = `${numFilter(finalScore * weight)} out of ${numFilter(weight)} point${weight === 1 ? '' : 's'}`;
                }
              } else {
                score = 'Not Answered';
              }
            } else {
              score = `${numFilter(weight)} point${weight === 1 ? '' : 's'}`;
            }

            if (answer && challenge.isQuestionTimed() && answer.timeTakenSec) {
              score += ` (answered in ${answer.timeTakenSec}s)`;
            }
          }

          summary.push(score);

          if (
            !challenge.isBonusTimed() &&
            self.maxAnswers() > 1 &&
            weight !== 0
          ) {
            if (self.allOrNothing) {
              summary.push('no partial credit');
            } else {
              summary.push('with partial credit');
            }
          }

          if (self.isMultipleChoice()) {
            let sel = self.updateMaxSelections();
            let weighted = self.weightedScoring ? ' weighted' : '';
            if (sel === 1) {
              summary.push(`single${weighted} answer`);
            } else {
              summary.push(`select up to ${sel}${weighted} answers`);
            }
          }

          if (
            self.options.lengthValidation &&
            self.options.lengthValidation.enabled
          ) {
            const actualLength =
              answer && answer.values.length > 0 ? answer.values[0].length : 0;
            summary.push(
              `length: ${actualLength}/${self.options.lengthValidation.expectedLength}`,
            );
          }

          if (challenge.isQuestionTimed()) {
            let limit = self._timeLimitSec(challenge);
            if (limit) {
              summary.push(`${limit}s time limit`);
              if (challenge.isBonusTimed()) {
                summary.push(
                  `bonus countdown at ${self._timeLimitStartSec(challenge)}s`,
                );
              }
            }
          }

          if (!answer && self.isText()) {
            summary.push(`${self.defaultValue ? 'with' : 'no'} default answer`);
          }

          return summary.join(', ');
        },
        _timeLimitSec(challenge) {
          return this.timeLimitSec || challenge.questionTimeLimitSec;
        },
        _timeLimitStartSec(challenge) {
          return angular.isNumber(this.timeLimitStartSec)
            ? this.timeLimitStartSec
            : challenge.questionTimeLimitStartSec || 0;
        },

        matcherTest() {
          if (!this.$matcherTest) {
            defineValue(this, '$matcherTest', {
              open: false,
              text: '',
              notes: {},
            });
          }
          return this.$matcherTest;
        },
        calculateLengthScore(answer) {
          if (
            !this.options.lengthValidation ||
            !this.options.lengthValidation.enabled
          ) {
            return 1;
          }

          const expectedLength = this.options.lengthValidation.expectedLength;

          if (expectedLength <= 0) {
            return 1;
          }

          // Handle both string input and answer object with values array
          let answerText = '';
          if (typeof answer === 'string') {
            answerText = answer;
          } else if (answer && answer.values && answer.values.length > 0) {
            answerText = answer.values[0];
          }

          const actualLength = answerText.length;

          return Math.min(1, actualLength / expectedLength);
        },

        getFinalScore(answer, ruleScore) {
          if (
            !this.options.lengthValidation ||
            !this.options.lengthValidation.enabled
          ) {
            return ruleScore;
          }

          const lengthScore = this.calculateLengthScore(answer);
          return (lengthScore + ruleScore) / 2;
        },
      },

      staticMethods: {
        getMatcherLabel(item, result) {
          const k =
            (item.negate ? 'not' : '') +
            (item.type || 'regex') +
            (result && !result.correct ? 'Incorrect' : '');
          return MATCHER_LABELS[k];
        },
        syncChoices(question) {
          if (
            question.choices &&
            question.maxSelections > question.choices.length
          ) {
            // revert to the default
            question.maxSelections = null;
          }
        },
      },

      relations: {},

      meta: {},
    });

    return QuizQuestion;
  });
